import type { WizardConfig } from '@/wizard/types.ts';
import { createWizard, type WizardContext, type WizardOptions, type WizardStepCode } from '@/wizard/create-wizard.ts';
import { inject, provide, onUnmounted, watchEffect, type InjectionKey, type MaybeRefOrGetter } from 'vue';

export type { WizardContext, WizardHeader, WizardMenuItem } from '@/wizard/create-wizard.ts';
export type { WizardConfig, WizardStep, WizardStepCode } from '@/wizard/types.ts';

const WIZARD_KEY: InjectionKey<WizardContext> = Symbol('wizard');
const WIZARD_NOT_PROVIDED_ERROR = 'useWizard() нужно вызывать внутри компонента, где вызван provideWizard().';

export function provideWizard<TData, TCode extends WizardStepCode = string>(
  config: WizardConfig<TData, TCode>,
  data: MaybeRefOrGetter<TData>,
  options?: WizardOptions<TCode>
): WizardContext<TCode> {
  const context = createWizard(config, data, options);
  provide(WIZARD_KEY, context as WizardContext);
  return context;
}

export function useWizard<TCode extends WizardStepCode = string>(): WizardContext<TCode> {
  const context = inject(WIZARD_KEY, null);

  if (!context) {
    throw new Error(WIZARD_NOT_PROVIDED_ERROR);
  }

  return context as WizardContext<TCode>;
}

export type WizardStepContext = Pick<WizardContext, 'isValid' | 'showErrors' | 'isLoading' | 'step'>;

export function useWizardStep(): WizardStepContext {
  const { isValid, showErrors, isLoading, step } = useWizard();

  return { isValid, showErrors, isLoading, step };
}

/** Опциональный шаг без обязательной валидации — выставляет isValid при монтировании. */
export function useWizardOptionalStep() {
  const { isValid } = useWizardStep();

  watchEffect(() => {
    isValid.value = true;
  });
}

export function useWizardComplete(handler: () => void | Promise<void>) {
  const wizard = useWizard();

  wizard.registerComplete(handler);
  onUnmounted(() => wizard.registerComplete());
}
